// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { Claim, ClaimItem, Extension } from '@medplum/fhirtypes';
import { CPT_SYSTEM } from '../utils/charge';
import { normalizeUsStateCode } from '../utils/us-states';

/**
 * CMS-1500 (02/12) professional claim, laid out box-for-box the way Stedi's 1500 Professional Claim
 * form collects it. A draft Claim stores one of these as its 837P source of truth: it is autofilled
 * from the patient, coverage, encounter and organization records when the draft is created, edited
 * on the claim detail page, and turned into the Stedi claim-create payload on submit.
 */
export interface Cms1500Form {
  version: 1;
  /** How the claim goes out. Only `electronic` (837P via Stedi) can be transmitted. */
  send: 'electronic' | 'mail';
  /** Claim identifier. Stedi's claims API only takes `chargeable` claims. */
  claimType: 'chargeable' | 'reporting' | 'subrogation';
  /** Payer — Stedi payer ID (`tradingPartnerServiceId`) and display name. */
  payer: { id: string; name: string };
  /** Box 1 — insurance program. */
  insType: Cms1500InsuranceType | '';
  /** Box 1a — payment responsibility level for this payer (P/S/T…). */
  respLevel: string;
  /** Box 1a — insured's ID number (member ID). */
  memberId: string;
  /** Boxes 2, 3, 5 — the patient. Only transmitted when the patient isn't the insured. */
  pat: Cms1500Patient;
  /** Box 6 — patient relationship to insured. */
  rel: Cms1500Relationship | '';
  /** Boxes 4, 7, 11, 11a–c — the insured (subscriber). */
  ins: Cms1500Insured;
  /** Boxes 9, 9a, 9d — other insured (coordination of benefits). */
  other: Cms1500OtherInsured | null;
  /** Box 10 — condition related to employment / auto accident / other, plus 10d claim codes. */
  cond: { emp: Cms1500YesNo; auto: Cms1500YesNo; autoState: string; other: Cms1500YesNo; codes: string[] };
  /** Box 12 — patient signature: release of information on file (`yes`) or informed consent. */
  sig12: 'yes' | 'informed' | '';
  /** Box 13 — insured signature: assignment of benefits. */
  sig13: 'yes' | 'no' | 'na' | '';
  /** Boxes 14–15 — clinical dates keyed by X12 date qualifier (431, 484, 304, 439…). */
  dates: Record<string, string>;
  /** Box 16 — dates patient unable to work. */
  unableFrom: string;
  unableTo: string;
  /** Box 18 — hospitalization dates. */
  hospFrom: string;
  hospTo: string;
  /** Box 17 — referring (DN), ordering (DK), supervising (DQ) and rendering (82) providers. */
  prov: Record<Cms1500ProviderRole, Cms1500Provider>;
  /** Box 19a — claim note qualifier and text. */
  n19q: string;
  n19: string;
  /** Box 19b — claim attachments (PWK). */
  att: Cms1500Attachment[];
  /** Box 22 — resubmission code (claim frequency) and the payer's original claim number. */
  resubQ: '1' | '7' | '8';
  origRef: string;
  /** Box 21 — ICD-10-CM diagnoses A through L. */
  dx: string[];
  /** Box 23 — prior authorization, referral, CLIA and mammography certification numbers. */
  priorAuth: string;
  referral: string;
  clia: string;
  mammo: string;
  /** Box 24 — service lines. */
  lines: Cms1500Line[];
  /** Box 25 — federal tax ID. */
  taxId: string;
  taxType: 'EIN' | 'SSN';
  /** Box 26 — patient account number (sent as the 837P patient control number). */
  acct: string;
  /** Box 27 — accept assignment. */
  assign: 'yes' | 'lab' | 'no' | '';
  /** Box 29 — amount paid by the patient. Omitted from the payload when blank or zero. */
  amtPaid: string;
  /** Box 31 — physician or supplier signature on file. */
  sig31: 'yes' | 'no' | '';
  /** Submitter (loop 1000A). */
  sub: Cms1500Submitter;
  /** Box 32 — service facility location. Optional; only sent when filled. */
  fac: Cms1500Facility;
  /** Box 33 — billing provider. */
  bill: Cms1500BillingProvider;
}

export type Cms1500YesNo = 'yes' | 'no' | '';
export type Cms1500InsuranceType = 'medicare' | 'medicaid' | 'tricare' | 'champva' | 'wc' | 'other';
export type Cms1500Relationship = 'self' | 'spouse' | 'child' | 'other';
export type Cms1500Sex = 'M' | 'F' | 'U' | '';
export type Cms1500ProviderRole = 'DN' | 'DK' | 'DQ' | '82';

export interface Cms1500Name {
  last: string;
  suffix: string;
  first: string;
  middle: string;
}

export interface Cms1500Address {
  line1: string;
  line2: string;
  city: string;
  state: string;
  zip: string;
}

export interface Cms1500Patient extends Cms1500Name, Cms1500Address {
  dob: string;
  sex: Cms1500Sex;
}

export interface Cms1500Insured extends Cms1500Patient {
  /** Box 11 — policy group or FECA number. */
  group: string;
  /** Box 11b — other claim ID qualifier and value. */
  ocq: string;
  ocid: string;
  /** Box 11c — insurance plan or program name. */
  planName: string;
}

export interface Cms1500OtherInsured extends Cms1500Name {
  /** Box 9a — other insured's policy or group number. */
  policy: string;
  /** Box 9d — other insurance plan or program name. */
  plan: string;
  /** Other insured's member ID — required by the 837P other-subscriber loop. */
  memberId: string;
  /** Other payer's ID — required by the 837P other-payer loop. */
  payerId: string;
  /** Other payer's responsibility level (S when this claim's payer is primary). */
  respLevel: string;
}

export interface Cms1500Provider extends Cms1500Name {
  npi: string;
  /** 0B — state license number. */
  lic: string;
  /** G2 — provider commercial number. */
  g2: string;
  /** 1G — provider UPIN (deprecated). */
  upin: string;
  /** NUCC taxonomy code — only sent for the rendering provider. */
  taxonomy: string;
}

export interface Cms1500Attachment {
  type: string;
  trans: string;
  ctrl: string;
}

export interface Cms1500Line {
  from: string;
  to: string;
  pos: string;
  emg: boolean;
  cpt: string;
  mods: string[];
  /** Diagnosis pointers as box-21 letters, in priority order (max 4). */
  ptrs: string[];
  /** Box 24F — line charge (total for the line, not per unit). */
  charge: string;
  units: string;
  unitType: 'UN' | 'MJ';
  epsdt: '' | 'Y' | 'N';
  renderNpi: string;
  lineAuth: string;
  note: string;
  ndc: string;
  ndcQty: string;
  ndcUnit: string;
  /** Box 20 — outside lab purchased service charge and the lab's identifier. */
  labCharge: string;
  labProviderId: string;
}

export interface Cms1500Submitter extends Cms1500Name {
  type: 'ind' | 'org';
  org: string;
  phone: string;
  fax: string;
  email: string;
  etin: string;
}

export interface Cms1500Facility extends Cms1500Address {
  org: string;
  npi: string;
  lic: string;
  g2: string;
  lu: string;
}

export interface Cms1500BillingProvider extends Cms1500Name, Cms1500Address {
  type: 'ind' | 'org';
  org: string;
  phone: string;
  npi: string;
  lic: string;
  g2: string;
  lu: string;
  taxonomy: string;
}

/**
 * Polaris bills every claim under one organization, so boxes 25 and 33 are fixed rather than read
 * from the tenant Organization (which has no EIN, NPI or billing address).
 */
export const POLARIS_BILLING_PROVIDER = {
  taxId: '41-4815927',
  taxType: 'EIN',
  bill: {
    type: 'org',
    org: 'Polaris Sleep',
    last: '',
    first: '',
    middle: '',
    suffix: '',
    line1: '1032 15TH ST NW # 418',
    line2: '',
    city: 'Washington',
    state: 'DC',
    zip: '20005-1502',
    phone: '301-246-2152',
    npi: '1851248363',
  },
} as const satisfies Pick<Cms1500Form, 'taxId' | 'taxType'> & {
  bill: Omit<Cms1500BillingProvider, 'lic' | 'g2' | 'lu' | 'taxonomy'>;
};

/** Overwrite boxes 25 and 33 with {@link POLARIS_BILLING_PROVIDER}. */
export function withPolarisBillingProvider(form: Cms1500Form): Cms1500Form {
  return {
    ...form,
    taxId: POLARIS_BILLING_PROVIDER.taxId,
    taxType: POLARIS_BILLING_PROVIDER.taxType,
    bill: { ...form.bill, ...POLARIS_BILLING_PROVIDER.bill },
  };
}

/** USPS code for a stored state ("Virginia" → "VA"); unrecognized values are kept so validation flags them. */
export function cms1500StateCode(state: string): string {
  return normalizeUsStateCode(state) ?? state;
}

/** Claim extension holding the serialized {@link Cms1500Form}. */
export const CLAIM_CMS1500_FORM_URL = 'http://polarissleep.com/fhir/extensions/cms1500-form';
/** Mirrors `CLAIM_PATIENT_PAID_URL` in the claims bot — box 29 is projected onto it. */
const CLAIM_PATIENT_PAID_URL = 'http://polarissleep.com/fhir/extensions/patient-paid';
const CLAIM_PLACE_OF_SERVICE_URL = 'http://polarissleep.com/fhir/extensions/place-of-service';
const ICD10_SYSTEM = 'http://hl7.org/fhir/sid/icd-10-cm';
const POS_SYSTEM = 'https://www.cms.gov/Medicare/Coding/place-of-service-codes/Place_of_Service_Code_Set';
const MAX_PATIENT_CONTROL_NUMBER_LENGTH = 17;

export const CMS1500_LETTERS = 'ABCDEFGHIJKL'.split('');
export const CMS1500_MAX_LINES = 50;

type Options = [string, string][];

export const CMS1500_STATES =
  'AL AK AZ AR CA CO CT DE DC FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY PR GU VI AS MP'.split(
    ' '
  );
export const CMS1500_RESP_LEVELS: Options = [
  ['P', 'P - Primary'],
  ['S', 'S - Secondary'],
  ['T', 'T - Tertiary'],
  ['A', 'A - Payer responsibility four'],
  ['B', 'B - Payer responsibility five'],
  ['C', 'C - Payer responsibility six'],
  ['D', 'D - Payer responsibility seven'],
  ['E', 'E - Payer responsibility eight'],
  ['F', 'F - Payer responsibility nine'],
  ['G', 'G - Payer responsibility ten'],
  ['H', 'H - Payer responsibility eleven'],
  ['U', 'U - Unknown'],
];
export const CMS1500_INSURANCE_TYPES: [Cms1500InsuranceType, string][] = [
  ['medicare', 'Medicare'],
  ['medicaid', 'Medicaid'],
  ['tricare', 'TRICARE'],
  ['champva', 'CHAMPVA'],
  ['wc', 'Workers Compensation, FECA, or Black Lung'],
  ['other', 'Other'],
];
export const CMS1500_CLAIM_CODES: Options = [
  ['AA', 'AA - Abortion performed due to rape'],
  ['AB', 'AB - Abortion performed due to incest'],
  ['AC', 'AC - Abortion due to serious fetal genetic defect'],
  ['AD', 'AD - Abortion due to life-endangering condition'],
  ['AE', 'AE - Abortion due to physical health of mother'],
  ['AF', 'AF - Abortion due to mental health of mother'],
  ['AG', 'AG - Abortion due to other reasons'],
  ['AH', 'AH - Elective abortion'],
  ['AI', 'AI - Sterilization'],
  ['W2', 'W2 - Duplicate of original bill'],
  ['W3', 'W3 - Level 1 appeal'],
  ['W4', 'W4 - Level 2 appeal'],
  ['W5', 'W5 - Level 3 appeal'],
];
export const CMS1500_OTHER_CLAIM_QUALIFIERS: Options = [['Y4', 'Y4 - Agency claim number']];
export const CMS1500_NOTE_QUALIFIERS: Options = [
  ['ADD', 'ADD - Additional information'],
  ['CER', 'CER - Certification narrative'],
  ['DCP', 'DCP - Goals, rehab potential, or discharge plans'],
  ['DGN', 'DGN - Diagnosis description'],
  ['TPO', 'TPO - Third party organization notes'],
];
export const CMS1500_RESUBMISSION_CODES: Options = [
  ['1', '1 - Original claim'],
  ['7', '7 - Replacement of prior claim'],
  ['8', '8 - Void / cancel of prior claim'],
];
export const CMS1500_PLACES_OF_SERVICE: Options = [
  ['02', '02 - Telehealth, not in patient home'],
  ['10', '10 - Telehealth in patient home'],
  ['11', '11 - Office'],
  ['12', '12 - Home'],
  ['19', '19 - Off-campus outpatient hospital'],
  ['22', '22 - On-campus outpatient hospital'],
  ['49', '49 - Independent clinic'],
  ['81', '81 - Independent laboratory'],
];
export const CMS1500_ATTACHMENT_TYPES: Options = [
  ['03', '03 - Justifies treatment beyond guidelines'],
  ['06', '06 - Initial assessment'],
  ['08', '08 - Plan of treatment'],
  ['09', '09 - Progress report'],
  ['B3', 'B3 - Physician order'],
  ['CK', 'CK - Consent form'],
  ['DB', 'DB - DME prescription'],
  ['DG', 'DG - Diagnostic report'],
  ['LA', 'LA - Laboratory results'],
  ['M1', 'M1 - Medical record attachment'],
  ['OZ', 'OZ - Support data for claim'],
  ['RR', 'RR - Radiology reports'],
];
export const CMS1500_ATTACHMENT_TRANSMISSIONS: Options = [
  ['AA', 'AA - Available on request at provider site'],
  ['BM', 'BM - By mail'],
  ['EL', 'EL - Electronically only'],
  ['EM', 'EM - Email'],
  ['FT', 'FT - File transfer'],
  ['FX', 'FX - By fax'],
];
/** Box 14 date qualifiers — only one may be entered. */
export const CMS1500_DATES_14: Options = [
  ['431', 'Onset of current symptoms or illness'],
  ['484', 'Last menstrual period'],
];
/** Box 15 date qualifiers shown by default. */
export const CMS1500_DATES_15: Options = [
  ['304', 'Last seen'],
  ['439', 'Accident'],
  ['453', 'Acute manifestation of a chronic condition'],
  ['454', 'Initial treatment'],
];
/** Box 15 date qualifiers behind "Show all dates". Disability dates (360/361) are box 16. */
export const CMS1500_DATES_15_MORE: Options = [
  ['090', 'Report start (assumed care)'],
  ['091', 'Report end (relinquished care)'],
  ['444', 'First visit or consultation'],
  ['455', 'Last X-ray'],
  ['471', 'Prescription'],
  ['297', 'Last worked'],
  ['296', 'Return to work'],
];
export const CMS1500_UNIT_TYPES: Options = [
  ['UN', 'UN - Unit'],
  ['MJ', 'MJ - Minutes'],
];
export const CMS1500_NDC_UNITS: Options = [
  ['UN', 'UN - Unit'],
  ['ML', 'ML - Milliliter'],
  ['GR', 'GR - Gram'],
  ['F2', 'F2 - International unit'],
  ['ME', 'ME - Milligram'],
];
export const CMS1500_PROVIDER_ROLES: [Cms1500ProviderRole, string][] = [
  ['82', 'Rendering provider'],
  ['DN', 'Referring provider'],
  ['DK', 'Ordering provider'],
  ['DQ', 'Supervising provider'],
];

export function blankCms1500Provider(): Cms1500Provider {
  return { last: '', suffix: '', first: '', middle: '', npi: '', lic: '', g2: '', upin: '', taxonomy: '' };
}

export function blankCms1500Line(pos = ''): Cms1500Line {
  return {
    from: '',
    to: '',
    pos,
    emg: false,
    cpt: '',
    mods: ['', '', '', ''],
    ptrs: [],
    charge: '',
    units: '1',
    unitType: 'UN',
    epsdt: '',
    renderNpi: '',
    lineAuth: '',
    note: '',
    ndc: '',
    ndcQty: '',
    ndcUnit: '',
    labCharge: '',
    labProviderId: '',
  };
}

export function blankCms1500OtherInsured(): Cms1500OtherInsured {
  return {
    last: '',
    suffix: '',
    first: '',
    middle: '',
    policy: '',
    plan: '',
    memberId: '',
    payerId: '',
    respLevel: 'S',
  };
}

export function blankCms1500Form(): Cms1500Form {
  const name = { last: '', suffix: '', first: '', middle: '' };
  const address = { line1: '', line2: '', city: '', state: '', zip: '' };
  return {
    version: 1,
    send: 'electronic',
    claimType: 'chargeable',
    payer: { id: '', name: '' },
    insType: 'other',
    respLevel: 'P',
    memberId: '',
    pat: { ...name, ...address, dob: '', sex: '' },
    rel: 'self',
    ins: { ...name, ...address, dob: '', sex: '', group: '', ocq: '', ocid: '', planName: '' },
    other: null,
    cond: { emp: 'no', auto: 'no', autoState: '', other: 'no', codes: [] },
    sig12: 'yes',
    sig13: 'yes',
    dates: {},
    unableFrom: '',
    unableTo: '',
    hospFrom: '',
    hospTo: '',
    prov: {
      '82': blankCms1500Provider(),
      DN: blankCms1500Provider(),
      DK: blankCms1500Provider(),
      DQ: blankCms1500Provider(),
    },
    n19q: '',
    n19: '',
    att: [],
    resubQ: '1',
    origRef: '',
    dx: CMS1500_LETTERS.map(() => ''),
    priorAuth: '',
    referral: '',
    clia: '',
    mammo: '',
    lines: [],
    taxId: '',
    taxType: 'EIN',
    acct: '',
    assign: 'yes',
    amtPaid: '',
    sig31: 'yes',
    sub: { type: 'org', org: '', ...name, phone: '', fax: '', email: '', etin: '' },
    fac: { org: '', ...address, npi: '', lic: '', g2: '', lu: '' },
    bill: { type: 'org', org: '', ...name, ...address, phone: '', npi: '', lic: '', g2: '', lu: '', taxonomy: '' },
  };
}

/** Parse a money/number string (`$1,250.00`, `3`) to a number, or null when it isn't one. */
export function cms1500Number(value: string | undefined): number | null {
  const n = Number.parseFloat(String(value ?? '').replace(/[$,\s]/g, ''));
  return Number.isNaN(n) ? null : n;
}

/** Box 28 — total charge, the sum of the service line charges. */
export function cms1500TotalCharge(form: Cms1500Form): number {
  return Math.round(form.lines.reduce((sum, line) => sum + (cms1500Number(line.charge) ?? 0), 0) * 100) / 100;
}

/**
 * Deterministic patient control number for an encounter — the 277CA/835 correlation key.
 * Alphanumeric, truncated to 17 characters as Stedi requires.
 */
export function cms1500PatientControlNumber(encounterId: string, claimSequence = 1): string {
  return `${encounterId}-${claimSequence}`.replace(/[^a-zA-Z0-9]/g, '').slice(0, MAX_PATIENT_CONTROL_NUMBER_LENGTH);
}

/** Read the CMS-1500 form stored on a Claim, filling any fields an older save didn't have. */
export function readCms1500Form(claim: Claim): Cms1500Form | undefined {
  const raw = claim.extension?.find((extension) => extension.url === CLAIM_CMS1500_FORM_URL)?.valueString;
  if (!raw) {
    return undefined;
  }
  try {
    return normalizeCms1500Form(JSON.parse(raw) as Partial<Cms1500Form>);
  } catch {
    return undefined;
  }
}

/** Merge a possibly partial form over a blank one so every nested field exists. */
export function normalizeCms1500Form(partial: Partial<Cms1500Form>): Cms1500Form {
  const blank = blankCms1500Form();
  const prov = { ...blank.prov };
  for (const [role] of CMS1500_PROVIDER_ROLES) {
    prov[role] = { ...blank.prov[role], ...partial.prov?.[role] };
  }
  const dx = CMS1500_LETTERS.map((_, index) => partial.dx?.[index] ?? '');
  const pat = { ...blank.pat, ...partial.pat };
  const ins = { ...blank.ins, ...partial.ins };
  const fac = { ...blank.fac, ...partial.fac };
  return withPolarisBillingProvider({
    ...blank,
    ...partial,
    version: 1,
    payer: { ...blank.payer, ...partial.payer },
    pat: { ...pat, state: cms1500StateCode(pat.state) },
    ins: { ...ins, state: cms1500StateCode(ins.state) },
    other: partial.other ? { ...blankCms1500OtherInsured(), ...partial.other } : null,
    cond: { ...blank.cond, ...partial.cond, codes: [...(partial.cond?.codes ?? [])] },
    dates: { ...partial.dates },
    prov,
    att: (partial.att ?? []).map((attachment) => ({
      type: attachment.type ?? '',
      trans: attachment.trans ?? '',
      ctrl: attachment.ctrl ?? '',
    })),
    dx,
    lines: (partial.lines ?? []).map((line) => {
      const merged = { ...blankCms1500Line(), ...line };
      return { ...merged, mods: [0, 1, 2, 3].map((index) => merged.mods[index] ?? ''), ptrs: [...merged.ptrs] };
    }),
    sub: { ...blank.sub, ...partial.sub },
    fac: { ...fac, state: cms1500StateCode(fac.state) },
    bill: { ...blank.bill, ...partial.bill },
  });
}

function servicedTiming(line: Cms1500Line): Pick<ClaimItem, 'servicedDate' | 'servicedPeriod'> {
  if (line.to && line.to !== line.from) {
    return { servicedPeriod: { start: line.from, end: line.to } };
  }
  return line.from ? { servicedDate: line.from } : {};
}

/**
 * Store the form on the Claim and re-project the native FHIR fields other screens read
 * (diagnosis, item, total, prior auth, patient paid, place of service) from it, so the Claim
 * never disagrees with what will be transmitted.
 */
export function writeCms1500Form(claim: Claim, form: Cms1500Form): Claim {
  const dx = form.dx.map((code) => code.trim().toUpperCase());
  const diagnosis = dx.filter(Boolean).map((code, index) => ({
    sequence: index + 1,
    diagnosisCodeableConcept: { coding: [{ system: ICD10_SYSTEM, code }] },
  }));
  const filledLetters = CMS1500_LETTERS.filter((_, index) => dx[index]);
  const defaultEncounter = claim.item?.find((item) => item.encounter?.length)?.encounter;
  const item: ClaimItem[] = form.lines.map((line, index) => {
    const charge = cms1500Number(line.charge) ?? 0;
    const units = cms1500Number(line.units) ?? 1;
    const modifiers = line.mods.map((mod) => mod.trim().toUpperCase()).filter(Boolean);
    const previous = claim.item?.[index];
    const code = line.cpt.trim().toUpperCase();
    const previousCoding = previous?.productOrService?.coding?.find((coding) => coding.code === code);
    return {
      sequence: index + 1,
      productOrService: {
        coding: [{ system: CPT_SYSTEM, code, display: previousCoding?.display }],
        text: previousCoding ? previous?.productOrService?.text : undefined,
      },
      ...(modifiers.length ? { modifier: modifiers.map((code) => ({ coding: [{ system: CPT_SYSTEM, code }] })) } : {}),
      ...servicedTiming(line),
      ...(line.pos ? { locationCodeableConcept: { coding: [{ system: POS_SYSTEM, code: line.pos }] } } : {}),
      diagnosisSequence: line.ptrs
        .map((letter) => filledLetters.indexOf(letter) + 1)
        .filter((sequence) => sequence > 0),
      quantity: { value: units },
      unitPrice: { value: units ? Math.round((charge / units) * 100) / 100 : charge, currency: 'USD' },
      net: { value: charge, currency: 'USD' },
      encounter: previous?.encounter ?? defaultEncounter,
    };
  });
  const amountPaid = cms1500Number(form.amtPaid) ?? 0;
  const replaced = new Set([CLAIM_CMS1500_FORM_URL, CLAIM_PATIENT_PAID_URL, CLAIM_PLACE_OF_SERVICE_URL]);
  const claimPos = form.lines.find((line) => line.pos)?.pos;
  const extension: Extension[] = [
    ...(claim.extension ?? []).filter((ext) => !replaced.has(ext.url)),
    ...(claimPos ? [{ url: CLAIM_PLACE_OF_SERVICE_URL, valueCode: claimPos }] : []),
    ...(amountPaid > 0
      ? [{ url: CLAIM_PATIENT_PAID_URL, valueMoney: { value: amountPaid, currency: 'USD' as const } }]
      : []),
    { url: CLAIM_CMS1500_FORM_URL, valueString: JSON.stringify(form) },
  ];
  return {
    ...claim,
    extension,
    diagnosis,
    item,
    total: { value: cms1500TotalCharge(form), currency: 'USD' },
    insurance: (claim.insurance ?? []).map((insurance, index) =>
      index === 0 ? { ...insurance, preAuthRef: form.priorAuth ? [form.priorAuth] : undefined } : insurance
    ),
  };
}
